import React from 'react';
import { useParams } from 'react-router-dom';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import { CLOSE_DIALOG_EVENT } from '@rio-cloud/rio-uikit/FrameDialog';
import usePostMessageSender from '@rio-cloud/rio-uikit/usePostMessageSender';

import Address from '@/features/common/components/Address';
import ContactPerson from '@/features/common/components/ContactPerson';
import { DialogErrorState } from '@/features/common/components/dialogs/common/DialogErrorState';
import { DialogFooter } from '@/features/common/components/dialogs/common/DialogFooter';
import { DialogLoadingState } from '@/features/common/components/dialogs/common/DialogLoadingState';
import { ASSIGNMENTS_ENDPOINT_PATH, MFE_DIALOG_PREFIX } from '@/features/common/components/dialogs/dialogConstants';
import type { Assignment } from '@/generated/GeneratedApiTypes';
import { useGetDialogDataQuery } from '@/services/dialogsApi';

const MARGIN_TEN_TOP_AND_BOTTOM = 'margin-top-10 margin-bottom-10';

const CustomerDetailsDialog: React.FC = () => {
    const { assetId } = useParams<{ assetId: string }>();
    const { sendMessageToParent } = usePostMessageSender();

    const { data, isLoading, isError, refetch, isFetching } = useGetDialogDataQuery({
        path: ASSIGNMENTS_ENDPOINT_PATH,
        id: assetId,
    });
    const assignment = data as Assignment;

    const handleCloseDialog = () => {
        sendMessageToParent({
            type: `${MFE_DIALOG_PREFIX}${CLOSE_DIALOG_EVENT}`,
            payload: `Closed customer details dialog for asset ID: ${assetId}`,
        });
    };

    if (isLoading || isFetching) {
        return <DialogLoadingState onClose={handleCloseDialog} />;
    }

    if (isError) {
        return <DialogErrorState onClose={handleCloseDialog} refetch={refetch} />;
    }
    const address = assignment?.fleet_account.address;
    const contactPerson = assignment.contact_person;

    return (
        <div className="bg-white min-height-200">
            <Dialog.Title title={assignment?.fleet_account.name} onCloseButtonClick={handleCloseDialog} />

            <Dialog.Body className={'padding-15 margin-left-15'}>
                {assignment && (
                    <div>
                        <div className="row col-xs-6">
                            <div className={MARGIN_TEN_TOP_AND_BOTTOM}>
                                <Address
                                    street={address?.street}
                                    city={address?.city}
                                    zip={address?.zip}
                                    countryCode={address?.country_code || ''}
                                    showLabel
                                />
                            </div>
                            <div className={MARGIN_TEN_TOP_AND_BOTTOM}>
                                <ContactPerson contactPerson={contactPerson} />
                            </div>
                        </div>
                    </div>
                )}
            </Dialog.Body>

            <DialogFooter onClose={handleCloseDialog} />
        </div>
    );
};

export default CustomerDetailsDialog;
